import { supabase } from '../lib/supabase';

// CRITICAL FIX: Get effective user ID for all queries
const getEffectiveUserId = async () => {
  // Check for impersonation first
  const impersonationData = localStorage.getItem('admin_impersonation');
  if (impersonationData) {
    const { impersonatedUserId } = JSON.parse(impersonationData);
    return impersonatedUserId;
  }
  
  // Fall back to authenticated user
  const { data: { user } } = await supabase?.auth?.getUser();
  return user?.id || null;
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return null;
  const camelCaseObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
    camelCaseObj[camelKey] = obj?.[key];
  }
  return camelCaseObj;
};

// Helper function to convert array of objects from snake_case to camelCase
const toCamelCaseArray = (arr) => {
  if (!arr) return [];
  return arr?.map(obj => toCamelCase(obj));
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  const snakeCaseObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    snakeCaseObj[snakeKey] = obj?.[key];
  }
  return snakeCaseObj;
};

export const budgetService = {
  // Budget Categories
  async getCategories() {
    try {
      const userId = await getEffectiveUserId();
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('budget_categories')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return toCamelCaseArray(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  async createCategory(categoryData) {
    try {
      const userId = await getEffectiveUserId();
      if (!userId) throw new Error('Not authenticated');

      const snakeCaseData = toSnakeCase(categoryData);
      const { data, error } = await supabase
        ?.from('budget_categories')
        ?.insert({
          ...snakeCaseData,
          user_id: userId,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  async updateCategory(id, categoryData) {
    try {
      const snakeCaseData = toSnakeCase(categoryData);
      const { data, error } = await supabase
        ?.from('budget_categories')
        ?.update(snakeCaseData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  async deleteCategory(id) {
    try {
      const { error } = await supabase
        ?.from('budget_categories')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },

  // Budget Records - CRITICAL FIX: Accept filters parameter
  async getRecords(userId = null, filters = {}) {
    try {
      // CRITICAL: Use getEffectiveUserId instead of passed userId
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) throw new Error('Not authenticated');

      let query = supabase
        ?.from('budget_records')
        ?.select(`
          *,
          budget_categories (
            id,
            name,
            category_type,
            color
          )
        `)
        ?.eq('user_id', effectiveUserId);

      // CRITICAL FIX: Apply date filters if provided
      if (filters?.startDate) {
        query = query?.gte('transaction_date', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('transaction_date', filters?.endDate);
      }

      query = query?.order('transaction_date', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(record => ({
        id: record?.id,
        userId: record?.user_id,
        categoryId: record?.category_id,
        amount: record?.amount,
        description: record?.description,
        transactionDate: record?.transaction_date,
        frequency: record?.frequency,
        notes: record?.notes,
        variableAmount: record?.variable_amount,
        createdAt: record?.created_at,
        updatedAt: record?.updated_at,
        budgetCategories: record?.budget_categories ? {
          id: record?.budget_categories?.id,
          name: record?.budget_categories?.name,
          categoryType: record?.budget_categories?.category_type,
          color: record?.budget_categories?.color
        } : null
      }));
    } catch (error) {
      console.error('Error fetching records:', error);
      throw error;
    }
  },

  async createRecord(recordData) {
    try {
      const userId = await getEffectiveUserId();
      if (!userId) throw new Error('Not authenticated');

      const snakeCaseData = toSnakeCase(recordData);
      const { data, error } = await supabase
        ?.from('budget_records')
        ?.insert({
          ...snakeCaseData,
          user_id: userId,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating record:', error);
      throw error;
    }
  },

  async updateRecord(id, recordData) {
    try {
      const snakeCaseData = toSnakeCase(recordData);
      const { data, error } = await supabase
        ?.from('budget_records')
        ?.update(snakeCaseData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating record:', error);
      throw error;
    }
  },

  async deleteRecord(id) {
    try {
      const { error } = await supabase
        ?.from('budget_records')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting record:', error);
      throw error;
    }
  },

  // Delete all income records and categories
  async deleteAllIncomeRecords() {
    try {
      const userId = await getEffectiveUserId();
      if (!userId) throw new Error('Not authenticated');

      // First, get all income category IDs for this user
      const { data: incomeCategories, error: categoriesError } = await supabase
        ?.from('budget_categories')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('category_type', 'income');

      if (categoriesError) throw categoriesError;

      if (incomeCategories && incomeCategories?.length > 0) {
        const categoryIds = incomeCategories?.map(cat => cat?.id);

        // Delete all budget records associated with these income categories
        const { error: recordsError } = await supabase
          ?.from('budget_records')
          ?.delete()
          ?.in('category_id', categoryIds);

        if (recordsError) throw recordsError;

        // Delete all income categories
        const { error: deleteCategoriesError } = await supabase
          ?.from('budget_categories')
          ?.delete()
          ?.eq('user_id', userId)
          ?.eq('category_type', 'income');

        if (deleteCategoriesError) throw deleteCategoriesError;
      }

      return { success: true, message: 'All income records deleted successfully' };
    } catch (error) {
      console.error('Error deleting all income records:', error);
      throw error;
    }
  },

  // Budget Forecasts
  async getForecasts() {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('budget_forecasts')?.select('*')?.eq('user_id', userId)?.order('forecast_month', { ascending: true });
    
    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async createForecast(forecastData) {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('budget_forecasts')?.insert({
        ...toSnakeCase(forecastData),
        user_id: userId
      })?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async updateForecast(forecastId, forecastData) {
    const { data, error } = await supabase?.from('budget_forecasts')?.update(toSnakeCase(forecastData))?.eq('id', forecastId)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  // Analytics
  async getMonthlyTotals(month) {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const startDate = new Date(month);
    const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

    const { data, error } = await supabase?.from('budget_records')?.select(`
        amount,
        budget_categories!inner (category_type)
      `)?.eq('user_id', userId)?.gte('transaction_date', startDate?.toISOString()?.split('T')?.[0])?.lte('transaction_date', endDate?.toISOString()?.split('T')?.[0]);
    
    if (error) throw error;

    const totals = {
      totalIncome: 0,
      totalExpenses: 0,
      netCashFlow: 0
    };

    data?.forEach(record => {
      const amount = parseFloat(record?.amount);
      if (record?.budget_categories?.category_type === 'income') {
        totals.totalIncome += amount;
      } else {
        totals.totalExpenses += amount;
      }
    });

    totals.netCashFlow = totals?.totalIncome - totals?.totalExpenses;
    return totals;
  },

  async getCategoryBreakdown(categoryType, startDate, endDate) {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('budget_records')?.select(`
        amount,
        budget_categories!inner (
          id,
          name,
          category_type,
          color
        )
      `)?.eq('user_id', userId)?.eq('budget_categories.category_type', categoryType)?.gte('transaction_date', startDate)?.lte('transaction_date', endDate);
    
    if (error) throw error;

    const breakdown = {};
    data?.forEach(record => {
      const catId = record?.budget_categories?.id;
      if (!breakdown?.[catId]) {
        breakdown[catId] = {
          id: catId,
          name: record?.budget_categories?.name,
          color: record?.budget_categories?.color,
          total: 0
        };
      }
      breakdown[catId].total += parseFloat(record?.amount);
    });

    return Object.values(breakdown);
  },

  // Portfolio Overview Metrics
  async getPortfolioOverview() {
    try {
      const userId = await getEffectiveUserId();
      if (!userId) throw new Error('Not authenticated');

      // Get all budget records with categories
      const { data, error } = await supabase
        ?.from('budget_records')
        ?.select(`
          amount,
          transaction_date,
          budget_categories!inner (
            category_type
          )
        `)
        ?.eq('user_id', userId);

      if (error) throw error;

      // Calculate totals
      let totalRevenue = 0;
      let totalCost = 0;
      let previousRevenue = 0;
      let previousCost = 0;

      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      data?.forEach(record => {
        const amount = parseFloat(record?.amount || 0);
        const recordDate = new Date(record?.transaction_date);
        const isIncome = record?.budget_categories?.category_type === 'income';

        if (recordDate >= currentMonthStart) {
          // Current month
          if (isIncome) {
            totalRevenue += amount;
          } else {
            totalCost += amount;
          }
        } else if (recordDate >= previousMonthStart && recordDate <= previousMonthEnd) {
          // Previous month for change calculation
          if (isIncome) {
            previousRevenue += amount;
          } else {
            previousCost += amount;
          }
        }
      });

      // Calculate percentage changes
      const revenueChange = previousRevenue > 0 
        ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 
        : 0;
      
      const costChange = previousCost > 0 
        ? ((totalCost - previousCost) / previousCost) * 100 
        : 0;

      return {
        totalRevenue,
        totalCost,
        revenueChange,
        costChange,
      };
    } catch (error) {
      console.error('Error fetching portfolio overview:', error);
      throw error;
    }
  },

  // NEW: Yearly budget analysis
  async getYearlyData(year) {
    try {
      const userId = await getEffectiveUserId();
      if (!userId) throw new Error('Not authenticated');

      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;

      const { data, error } = await supabase
        ?.from('budget_records')
        ?.select(`
          amount,
          transaction_date,
          budget_categories!inner (
            category_type,
            name
          )
        `)
        ?.eq('user_id', userId)
        ?.gte('transaction_date', startDate)
        ?.lte('transaction_date', endDate)
        ?.order('transaction_date', { ascending: true });

      if (error) throw error;

      // Group by month
      const monthlyData = {};
      for (let month = 0; month < 12; month++) {
        const monthKey = `${year}-${String(month + 1)?.padStart(2, '0')}`;
        monthlyData[monthKey] = {
          month: monthKey,
          income: 0,
          expenses: 0,
          savings: 0,
          netProfit: 0
        };
      }

      data?.forEach(record => {
        const amount = parseFloat(record?.amount || 0);
        const monthKey = record?.transaction_date?.substring(0, 7); // YYYY-MM
        
        if (monthlyData?.[monthKey]) {
          const isSaving = record?.budget_categories?.name?.toLowerCase()?.includes('saving') ||
                          record?.budget_categories?.name?.toLowerCase()?.includes('reserve');
          
          if (record?.budget_categories?.category_type === 'income') {
            if (isSaving) {
              monthlyData[monthKey].savings += amount;
            } else {
              monthlyData[monthKey].income += amount;
            }
          } else {
            monthlyData[monthKey].expenses += amount;
          }
        }
      });

      // Calculate net profit for each month
      Object.values(monthlyData)?.forEach(month => {
        month.netProfit = month?.income - month?.expenses;
      });

      return Object.values(monthlyData);
    } catch (error) {
      console.error('Error fetching yearly data:', error);
      throw error;
    }
  },

  // NEW: Year-over-year comparison
  async getYearOverYearComparison(currentYear, previousYear) {
    try {
      const userId = await getEffectiveUserId();
      if (!userId) throw new Error('Not authenticated');

      const [currentYearData, previousYearData] = await Promise.all([
        this.getYearlyTotals(currentYear),
        this.getYearlyTotals(previousYear)
      ]);

      const incomeGrowth = previousYearData?.totalIncome > 0
        ? ((currentYearData?.totalIncome - previousYearData?.totalIncome) / previousYearData?.totalIncome) * 100
        : 0;

      const expenseGrowth = previousYearData?.totalExpenses > 0
        ? ((currentYearData?.totalExpenses - previousYearData?.totalExpenses) / previousYearData?.totalExpenses) * 100
        : 0;

      const profitGrowth = previousYearData?.netProfit > 0
        ? ((currentYearData?.netProfit - previousYearData?.netProfit) / previousYearData?.netProfit) * 100
        : 0;

      return {
        currentYear: currentYearData,
        previousYear: previousYearData,
        growth: {
          income: incomeGrowth,
          expenses: expenseGrowth,
          profit: profitGrowth
        }
      };
    } catch (error) {
      console.error('Error calculating year-over-year comparison:', error);
      throw error;
    }
  },

  // NEW: Get yearly totals
  async getYearlyTotals(year) {
    try {
      const userId = await getEffectiveUserId();
      if (!userId) throw new Error('Not authenticated');

      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;

      const { data, error } = await supabase
        ?.from('budget_records')
        ?.select(`
          amount,
          budget_categories!inner (
            category_type,
            name
          )
        `)
        ?.eq('user_id', userId)
        ?.gte('transaction_date', startDate)
        ?.lte('transaction_date', endDate);

      if (error) throw error;

      let totalIncome = 0;
      let totalExpenses = 0;
      let totalSavings = 0;

      data?.forEach(record => {
        const amount = parseFloat(record?.amount || 0);
        const isSaving = record?.budget_categories?.name?.toLowerCase()?.includes('saving') ||
                        record?.budget_categories?.name?.toLowerCase()?.includes('reserve');
        
        if (record?.budget_categories?.category_type === 'income') {
          if (isSaving) {
            totalSavings += amount;
          } else {
            totalIncome += amount;
          }
        } else {
          totalExpenses += amount;
        }
      });

      const netProfit = totalIncome - totalExpenses;
      const averageMonthlyIncome = totalIncome / 12;
      const averageMonthlyExpenses = totalExpenses / 12;

      return {
        totalIncome,
        totalExpenses,
        totalSavings,
        netProfit,
        averageMonthlyIncome,
        averageMonthlyExpenses,
        year
      };
    } catch (error) {
      console.error('Error fetching yearly totals:', error);
      throw error;
    }
  }
};